import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  LinearProgress,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import { People } from '@mui/icons-material';
import { getSubscriptionUsage, SubscriptionUsage } from '../services/subscription';

const PlanUsageCard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [usage, setUsage] = useState<SubscriptionUsage | null>(null);

  useEffect(() => {
    const fetchUsage = async () => {
      try {
        setLoading(true);
        const usageData = await getSubscriptionUsage();
        setUsage(usageData);
        setError(null);
      } catch (err: any) {
        console.error('Erro ao buscar uso da assinatura:', err);
        setError('Não foi possível carregar informações do plano');
      } finally {
        setLoading(false);
      }
    };

    fetchUsage();
  }, []);

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error || !usage) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error">{error || 'Erro ao carregar dados'}</Alert>
        </CardContent>
      </Card>
    );
  }

  if (!usage.hasActiveSubscription) {
    return (
      <Card>
        <CardContent>
          <Alert severity="warning">
            Nenhuma assinatura ativa encontrada
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const progressColor = usage.percentageUsed >= 100 ? 'error' : 
                       usage.percentageUsed >= 80 ? 'warning' : 'primary';

  return (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" mb={2}>
          <People color="primary" sx={{ mr: 1, fontSize: 28 }} />
          <Typography variant="h6" fontWeight="bold">
            Uso do Plano
          </Typography>
        </Box>

        <Box mb={2}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Plano Atual
          </Typography>
          <Chip 
            label={usage.planName} 
            color="primary" 
            sx={{ fontWeight: 'bold' }}
          />
        </Box>

        <Box mb={3}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="body2" color="text.secondary">
              Usuários
            </Typography>
            <Typography variant="h6" fontWeight="bold">
              {usage.currentUsers} / {usage.maxUsers || '∞'}
            </Typography>
          </Box>
          
          <LinearProgress
            variant="determinate"
            value={Math.min(usage.percentageUsed, 100)}
            color={progressColor}
            sx={{ 
              height: 10, 
              borderRadius: 5,
              bgcolor: 'action.hover',
            }}
          />
          
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {usage.percentageUsed.toFixed(0)}% utilizado
          </Typography>
        </Box>

        {usage.percentageUsed >= 100 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Você atingiu o limite de usuários do seu plano. 
            Para adicionar mais colaboradores, escolha um plano maior.
          </Alert>
        )}

        <Typography variant="caption" color="text.secondary" display="block" mt={2}>
          Inclui 1 proprietário + colaboradores ativos
        </Typography>
      </CardContent>
    </Card>
  );
};

export default PlanUsageCard;
